import React, { useEffect, useState, useRef, useContext } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Search, RefreshCw, Filter, Phone, Mail, User, Stethoscope, Building, 
    MoreVertical, Eye, Lock, Unlock, Trash2
} from 'lucide-react';

const formatPhone = (value) => {
    if (!value) return '+992 ';
    const numbers = value.replace(/\D/g, '');
    const tjNum = numbers.startsWith('992') ? numbers.slice(3) : numbers;
    let res = '+992 ';
    if (tjNum.length > 0) res += tjNum.substring(0, 3);
    if (tjNum.length > 3) res += '-' + tjNum.substring(3, 5);
    if (tjNum.length > 5) res += '-' + tjNum.substring(5, 7);
    if (tjNum.length > 7) res += '-' + tjNum.substring(7, 9);
    return res;
};

const CustomSearchSelect = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 10);
    const selectedLabel = options.find(o => String(o.value) === String(value))?.label || placeholder;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <div onClick={() => setIsOpen(!isOpen)} style={{...selectStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: 'white', minHeight: '38px'}}>
                <span style={{color: value ? '#111827' : '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9rem'}}>{selectedLabel}</span>
                <span style={{fontSize: '10px', color: '#9ca3af'}}>▼</span>
            </div>
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 100 }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{position: 'relative'}}>
                            <Search size={14} style={{position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}}/>
                            <input 
                                type="text" placeholder="Поиск..." value={search} onChange={e => setSearch(e.target.value)} 
                                style={{ width: '100%', padding: '8px 8px 8px 28px', borderRadius: '6px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '0.85rem' }} 
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <div 
                            onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }} 
                            style={{ padding: '10px 12px', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', transition: 'background 0.2s' }}
                            onMouseOver={e => e.target.style.background = '#f9fafb'} onMouseOut={e => e.target.style.background = 'transparent'}
                        >
                            {placeholder}
                        </div>
                        {filteredOptions.map(opt => (
                            <div 
                                key={opt.value} 
                                onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }} 
                                style={{ padding: '10px 12px', cursor: 'pointer', borderTop: '1px solid #f3f4f6', background: String(value) === String(opt.value) ? '#eff6ff' : 'transparent', color: '#374151', fontSize: '0.9rem', transition: 'background 0.2s' }}
                                onMouseOver={e => { if (String(value) !== String(opt.value)) e.target.style.background = '#f9fafb' }} 
                                onMouseOut={e => { if (String(value) !== String(opt.value)) e.target.style.background = 'transparent' }}
                            >
                                {opt.label}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && <div style={{ padding: '10px 12px', color: '#9ca3af', textAlign: 'center', fontSize: '0.9rem' }}>Ничего не найдено</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

const Doctors = () => {
    const [doctors, setDoctors] = useState([]);
    const [hospitals, setHospitals] = useState([]);
    const { user } = useContext(AuthContext);
    
    const [allPatients, setAllPatients] = useState([]);
    const [allPrescriptions, setAllPrescriptions] = useState([]);
    
    const [isLoading, setIsLoading] = useState(false);
    
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({ hospital_id: '', department: '', specialization: '' });

    const [visibleCount, setVisibleCount] = useState(10);

    useEffect(() => {
        setVisibleCount(10);
    }, [searchQuery, filters]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [usersRes, hospitalsRes, patientsRes, prescriptionsRes] = await Promise.all([
                api.get('/users?role=doctor'),
                api.get('/hospitals'),
                api.get('/patients'),
                api.get('/prescriptions')
            ]);
            setDoctors(usersRes.data);
            setHospitals(hospitalsRes.data);
            setAllPatients(patientsRes.data);
            setAllPrescriptions(prescriptionsRes.data);
        } catch (error) {
            console.error("Ошибка загрузки:", error);
            alert("Не удалось обновить данные");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // --- ЛОГИКА ФИЛЬТРАЦИИ ДЛЯ ГЛАВВРАЧА ---
    // Находим больницу, в которой этот пользователь является главврачом
    const chiefHospital = user?.role === 'chief_doctor' ? hospitals.find(h => h.chief_doctor_id === user.id) : null;
    
    // Оставляем только тех врачей, которые принадлежат этой больнице (для остальных ролей оставляем всех)
    const myDoctors = user?.role === 'chief_doctor' 
        ? doctors.filter(d => chiefHospital && d.hospital_id === chiefHospital.id)
        : doctors;

    const uniqueDepartments = [...new Set(myDoctors.map(d => d.department).filter(Boolean))];
    const uniqueSpecializations = [...new Set(myDoctors.map(d => d.specialization).filter(Boolean))];

    const hospitalOptions = hospitals.map(h => ({ value: h.id, label: h.name }));
    const deptOptions = uniqueDepartments.map(d => ({ value: d, label: d }));
    const specOptions = uniqueSpecializations.map(s => ({ value: s, label: s }));

    const filteredDoctors = myDoctors.filter(doc => {
        const matchSearch = (
            (doc.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (doc.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (doc.phone || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
        const matchHospital = filters.hospital_id ? doc.hospital_id === parseInt(filters.hospital_id) : true;
        const matchDept = filters.department ? doc.department === filters.department : true;
        const matchSpec = filters.specialization ? doc.specialization === filters.specialization : true;
        return matchSearch && matchHospital && matchDept && matchSpec;
    });

    const visibleDoctors = filteredDoctors.slice(0, visibleCount);

    const toggleMenu = (e, id) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === id ? null : id);
    };

    const handleViewDetails = (e, doc) => {
        e.stopPropagation();
        setSelectedDoctor(doc);
        setOpenMenuId(null);
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Вы уверены, что хотите удалить этого врача?")) return;
        try {
            await api.delete(`/users/${id}`);
            setDoctors(doctors.filter(d => d.id !== id));
        } catch (e) { alert("Ошибка удаления"); }
    };

    const handleToggleStatus = async (doc) => {
        const newStatus = !doc.is_active;
        try {
            await api.put(`/users/${doc.id}`, { is_active: newStatus });
            setDoctors(doctors.map(d => d.id === doc.id ? { ...d, is_active: newStatus } : d));
        } catch (e) { alert("Ошибка изменения статуса"); }
    };

    const DoctorDetailsModal = ({ doctor, onClose, patients, prescriptions }) => {
        if (!doctor) return null;

        const pCount = patients.filter(p => p.doctor_id === doctor.id).length;
        const rCount = prescriptions.filter(r => r.doctor_id === doctor.id).length;

        return (
            <div className="modal-overlay" onClick={onClose} style={overlayStyle}>
                <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
                    
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginTop: '0', marginBottom: '4px' }}>Подробная информация о враче</h3>
                        <p style={{ color: '#6b7280' }}>{doctor.full_name}</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '30px' }}>
                        <div><p style={detailLabel}>Email:</p><p style={detailValue}>{doctor.email}</p></div>
                        <div><p style={detailLabel}>Специализация:</p><p style={detailValue}>{doctor.specialization || 'Не указано'}</p></div>
                        <div><p style={detailLabel}>Больница:</p><p style={detailValue}>{doctor.hospital?.name || 'Не указано'}</p></div>
                        <div><p style={detailLabel}>Отделение:</p><p style={detailValue}>{doctor.department || 'Не указано'}</p></div>
                        <div><p style={detailLabel}>Кабинет:</p><p style={detailValue}>{doctor.cabinet || 'Не указано'}</p></div>
                        <div><p style={detailLabel}>Телефон:</p><p style={detailValue}>{formatPhone(doctor.phone)}</p></div>
                        <div>
                            <p style={detailLabel}>Статус:</p>
                            <div style={{ marginTop: '5px' }}>
                                <span className={`status-badge ${doctor.is_active ? 'badge-active' : 'badge-blocked'}`} 
                                      style={doctor.is_active ? {background: '#000', color: '#fff'} : {background: '#ef4444', color: '#fff'}}>
                                    {doctor.is_active ? 'Активен' : 'Заблокирован'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <p style={detailLabel}>Статистика врача:</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                            <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '12px' }}>
                                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '4px' }}>Пациентов</p>
                                <p style={{ color: '#2563eb', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{pCount}</p>
                            </div>
                            <div style={{ background: '#fdf4ff', padding: '16px', borderRadius: '12px' }}>
                                <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '4px' }}>Рецептов выписано</p>
                                <p style={{ color: '#c026d3', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{rCount}</p>
                            </div>
                        </div>
                    </div>

                    <button onClick={onClose} style={closeBtnStyle}>Закрыть</button>
                </div>
            </div>
        );
    };

    return (
        <div className="content-wrapper">
            <style>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
            
            <div className="card doc-controls-card" style={{margin: '0'}}>
                <div className="card-header doc-header-flex">
                    <div className="doc-title-block">
                        <h4 className="card-title text-blue" style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <Stethoscope size={24} />
                            Управление врачами
                        </h4>
                        <p className="card-subtitle">Всего врачей: {myDoctors.length} • Активных: {myDoctors.filter(d => d.is_active).length}</p>
                    </div>
                    <button className="flex-none btn-outline btn-sm" onClick={fetchData} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                        <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} /> Обновить
                    </button>
                </div>
                
                <div className="card-body doc-filter-body">
                    <div className="search-wrapper">
                        <Search className="search-icon" size={18} />
                        <input 
                            type="text" className="search-input" 
                            placeholder="Поиск по имени, email, больнице..." 
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    
                    <div className="filter-actions">
                        <button className="flex-none btn-outline btn-sm" onClick={() => setShowFilters(!showFilters)} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                            <Filter size={16} /> Фильтры
                        </button>
                        <span className="filter-count">Найдено: {filteredDoctors.length} из {myDoctors.length}</span>
                    </div>

                    {showFilters && (
                         <div style={{ paddingTop: '16px', borderTop: '1px solid #eee', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                            {/* Скрываем фильтр по больнице для Главврача */}
                            {user?.role !== 'chief_doctor' && (
                                <CustomSearchSelect 
                                    value={filters.hospital_id} 
                                    onChange={val => setFilters({...filters, hospital_id: val})} 
                                    options={hospitalOptions} 
                                    placeholder="Все больницы" 
                                />
                            )}
                            <CustomSearchSelect 
                                value={filters.department} 
                                onChange={val => setFilters({...filters, department: val})} 
                                options={deptOptions} 
                                placeholder="Все отделения" 
                            />
                            <CustomSearchSelect 
                                value={filters.specialization} 
                                onChange={val => setFilters({...filters, specialization: val})} 
                                options={specOptions} 
                                placeholder="Все специальности" 
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="doctors-list" id="doctorsList">
                {visibleDoctors.length === 0 && !isLoading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', width: '100%', gridColumn: '1 / -1' }}>Ничего не найдено</div>
                )}
                
                {visibleDoctors.map(doc => (
                    <div key={doc.id} className="card doc-item-card">
                        <div className="card-body doc-item-body">
                            <div className="doc-main-info">
                                <div className="doc-header-row">
                                    <div className="doc-profile">
                                        <div className="doc-avatar bg-blue-light text-blue" style={{display:'flex', justifyContent:'center', alignItems:'center', fontWeight:'bold', fontSize:'1.2rem'}}>
                                            {doc.full_name ? doc.full_name.charAt(0) : '?'}
                                        </div>
                                        <div className="doc-names">
                                            <h3 className="doc-name">{doc.full_name}</h3>
                                            <p className="doc-spec">{doc.specialization || 'Специализация не указана'}</p>
                                        </div>
                                        <span className={`status-badge ${doc.is_active ? 'badge-active' : 'badge-blocked'}`} 
                                              style={doc.is_active ? {background: '#000', color: '#fff'} : {background: '#ef4444', color: '#fff'}}>
                                            {doc.is_active ? 'Активен' : 'Заблокирован'}
                                        </span>
                                    </div>
                                    
                                    <div style={{position: 'relative'}}>
                                        <button className="btn-icon-only btn-sm btn-more" onClick={(e) => toggleMenu(e, doc.id)}>
                                            <MoreVertical size={16} />
                                        </button>

                                        {openMenuId === doc.id && (
                                            <div className="dropdown-menu" style={dropdownStyle}>
                                                <button onClick={(e) => handleViewDetails(e, doc)} style={menuItemStyle}>
                                                    <Eye size={16} /> Подробная информация
                                                </button>
                                                <button onClick={() => handleToggleStatus(doc)} style={{...menuItemStyle, color: doc.is_active ? '#f59e0b' : '#10b981'}}>
                                                    {doc.is_active ? <><Lock size={16}/> Заблокировать</> : <><Unlock size={16}/> Разблокировать</>}
                                                </button>
                                                {/* Кнопку удалить оставляем доступной для управления */}
                                                <button onClick={() => handleDelete(doc.id)} style={{...menuItemStyle, color: '#ef4444', borderTop: '1px solid #f3f4f6'}}>
                                                    <Trash2 size={16} /> Удалить
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="doc-details-grid">
                                    <div className="detail-item">
                                        <Mail size={16} /> {doc.email}
                                    </div>
                                    <div className="detail-item">
                                        <Phone size={16} /> {formatPhone(doc.phone)}
                                    </div>
                                    <div className="detail-item">
                                        <Building size={16} /> 
                                        {doc.hospital ? doc.hospital.name : "Нет больницы"} 
                                        {doc.cabinet && ` • Каб. ${doc.cabinet}`}
                                    </div>
                                    <div className="detail-item">
                                        <User size={16} /> Пациенты: {allPatients.filter(p => p.doctor_id === doc.id).length} • Рецепты: {allPrescriptions.filter(r => r.doctor_id === doc.id).length}
                                    </div>
                                </div>

                                <div className="doc-footer-meta">
                                    Зарегистрирован: {new Date(doc.created_at).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {!isLoading && visibleDoctors.length < filteredDoctors.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}

            <DoctorDetailsModal 
                doctor={selectedDoctor} 
                onClose={() => setSelectedDoctor(null)} 
                patients={allPatients} 
                prescriptions={allPrescriptions} 
            />
        </div>
    );
};

const selectStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none' };
const dropdownStyle = { position: 'absolute', right: 0, top: '30px', width: '230px', backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', borderRadius: '8px', border: '1px solid #e5e7eb', zIndex: 50, overflow: 'hidden' };
const menuItemStyle = { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 16px', border: 'none', background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem', color: '#374151', transition: 'background 0.2s' };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(2px)' };
const modalStyle = { backgroundColor: 'white', borderRadius: '16px', width: '600px', maxWidth: '95%', padding: '32px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', position: 'relative' };
const detailLabel = { fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px', margin: 0 };
const detailValue = { fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 };
const closeBtnStyle = { width: '100%', padding: '12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', cursor: 'pointer' };

export default Doctors;